/**
 * Brand Colors - Single Source of Truth
 *
 * All application colors are defined here.
 * To change colors, simply update the hex values in this file.
 */

export const COLORS = {
  // Primary Color - Main brand color (slate blue)
  primary: {
    50: '#f5f7f9',
    100: '#e9ecf1',
    200: '#d3dae4',
    300: '#b0bfcf',
    400: '#879fb6',
    500: '#647991',
    600: '#556881',
    700: '#47566b',
    800: '#3d4959',
    900: '#353e4c',
    DEFAULT: '#647991',
  },

  // Secondary Color - Accent color (light blue)
  secondary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3',
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
    DEFAULT: '#64B5F6',
  },

  // Text Colors
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    tertiary: '#9ca3af',
    inverse: '#ffffff',
  },

  // Semantic Colors
  success: {
    light: '#dcfce7',
    DEFAULT: '#16a34a',
    dark: '#15803d',
  },

  error: {
    lighter: '#fecaca',
    light: '#fee2e2',
    DEFAULT: '#dc2626',
    dark: '#b91c1c',
    soft: '#ef4444', // Softer, less harsh red for warnings
  },

  warning: {
    light: '#fef3c7',
    DEFAULT: '#f59e0b',
    dark: '#d97706',
  },

  info: {
    light: '#dbeafe',
    DEFAULT: '#3b82f6',
    dark: '#2563eb',
    darker: '#1e40af',
  },

  // Gray scale (neutral colors used throughout the app)
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // Common colors
  common: {
    white: '#ffffff',
    whiteTransparent60: 'rgba(255, 255, 255, 0.6)',
    whiteTransparent80: 'rgba(255, 255, 255, 0.8)',
    black: '#000000',
    transparent: 'transparent',
  },

  // Brand colors (external services)
  brand: {
    whatsapp: '#25D366',
    whatsappHover: '#20BA5A',
  },

  // Icon colors
  icon: {
    favorite: '#FFD700', // Gold star for favorites
  },

  // Overlay colors (for modals, backdrops, etc.)
  overlay: {
    light: 'rgba(0, 0, 0, 0.3)',
    medium: 'rgba(0, 0, 0, 0.5)',
    dark: 'rgba(0, 0, 0, 0.7)',
    darker: 'rgba(0, 0, 0, 0.8)',
  },
} as const;

/**
 * Semantic Theme System
 *
 * THEME provides context-aware color tokens that automatically use the right colors
 * for different backgrounds. This makes it easier to write consistent, maintainable code.
 *
 * Usage:
 * - Use THEME.light for light backgrounds (modals, cards with white/gray backgrounds)
 * - Use THEME.dark for dark backgrounds (main app background)
 * - For semantic colors (success, error, etc.), use THEME.semantic
 *
 * Example:
 * ```typescript
 * // Light modal
 * <Modal theme="light">
 *   <p style={{ color: THEME.light.text.primary }}>Main text</p>
 *   <p style={{ color: THEME.light.text.secondary }}>Helper text</p>
 * </Modal>
 *
 * // Dark app background
 * <div style={{ color: THEME.dark.text.primary }}>Main text</div>
 * ```
 */
export const THEME = {
  /**
   * Light theme - Use for light backgrounds (modals, cards, tooltips)
   * Background: White or light gray
   */
  light: {
    text: {
      primary: COLORS.gray[900],       // Main headings and important text
      secondary: COLORS.gray[700],     // Body text, labels
      tertiary: COLORS.gray[600],      // Helper text, less important content
      muted: COLORS.gray[500],         // Very subtle text, placeholders
      disabled: COLORS.gray[400],      // Disabled state text
    },
    background: {
      primary: COLORS.common.white,    // Main background
      secondary: COLORS.gray[50],      // Elevated surfaces, cards
      tertiary: COLORS.gray[100],      // Hover states, selected items
      hover: COLORS.gray[100],         // Interactive hover background
    },
    border: {
      default: COLORS.gray[200],       // Standard borders
      strong: COLORS.gray[300],        // Emphasized borders
      subtle: COLORS.gray[100],        // Very light borders
    },
  },

  /**
   * Dark theme - Use for dark backgrounds (main application)
   * Background: Dark gray or black
   */
  dark: {
    text: {
      primary: COLORS.common.white,    // Main headings and important text
      secondary: COLORS.gray[300],     // Body text, labels
      tertiary: COLORS.gray[400],      // Helper text, less important content
      muted: COLORS.gray[500],         // Very subtle text, placeholders
      disabled: COLORS.gray[600],      // Disabled state text
    },
    background: {
      primary: '#1a1a1a',              // Main dark background
      secondary: COLORS.gray[800],     // Elevated surfaces, cards on dark
      tertiary: COLORS.gray[700],      // Hover states, selected items
      hover: COLORS.gray[700],         // Interactive hover background
    },
    border: {
      default: COLORS.gray[600],       // Standard borders
      strong: COLORS.gray[500],        // Emphasized borders
      subtle: COLORS.gray[700],        // Very light borders
    },
  },

  /**
   * Semantic colors - Same for both light and dark themes
   * Use these for status indicators, alerts, and feedback
   */
  semantic: {
    success: {
      bg: COLORS.success.light,        // Success background
      text: COLORS.success.dark,       // Success text (on light bg)
      main: COLORS.success.DEFAULT,    // Success primary color
    },
    error: {
      bg: COLORS.error.light,          // Error background
      text: COLORS.error.dark,         // Error text (on light bg)
      main: COLORS.error.DEFAULT,      // Error primary color
      soft: COLORS.error.soft,         // Softer error color for less critical warnings
      hover: COLORS.error.lighter,     // Error hover state
    },
    warning: {
      bg: COLORS.warning.light,        // Warning background
      text: COLORS.warning.dark,       // Warning text (on light bg)
      main: COLORS.warning.DEFAULT,    // Warning primary color
    },
    info: {
      bg: COLORS.info.light,           // Info background
      text: COLORS.info.darker,        // Info text (on light bg)
      main: COLORS.info.DEFAULT,       // Info primary color
    },
  },

  /**
   * Interactive elements - Buttons, links, and interactive components
   * These work on both light and dark backgrounds
   */
  interactive: {
    primary: {
      main: COLORS.primary.DEFAULT,
      hover: COLORS.primary[600],
      active: COLORS.primary[700],
      disabled: COLORS.primary[300],
      text: COLORS.common.white,       // Text on primary buttons
    },
    secondary: {
      main: COLORS.secondary.DEFAULT,
      hover: COLORS.secondary[600],
      active: COLORS.secondary[700],
      disabled: COLORS.secondary[300],
      text: COLORS.common.white,       // Text on secondary buttons
    },
  },

  /**
   * Special purpose colors
   */
  special: {
    overlay: COLORS.overlay,           // Modal overlays, backdrops
    brand: COLORS.brand,               // External brand colors (WhatsApp, etc.)
    icon: COLORS.icon,                 // Special icon colors
  },
} as const;

/**
 * Type helper to get theme colors
 * Usage: const lightTheme = getTheme('light');
 */
export type ThemeVariant = 'light' | 'dark';
export type ThemeColors = typeof THEME.light | typeof THEME.dark;

export function getTheme(variant: ThemeVariant): ThemeColors {
  return THEME[variant];
}
